/* eslint-disable react-hooks/exhaustive-deps */
import { motion } from "framer-motion";
import { FaSpinner } from "react-icons/fa";
import Footer from "../components/Footer";
import { useState, useEffect } from "react";
import axios from "axios";

const About = () => {
  const [missionVisionData, setMissionVisionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UImissionVision`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;

  // Fetch mission/vision data
  useEffect(() => {
    const fetchMissionVisionData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(API_URL);
        const data = response.data.results || response.data;

        // Filter only active records
        const activeData = data.filter(item => item.isActive === true);

        // Load images for active records
        const dataWithImages = await Promise.all(
          activeData.map(async (item) => {
            let mainLogoUrl = "";
            
            // Load main logo
            if (item.mainLogo?.id) {
              try {
                const imgRes = await axios.get(`${API_MEDIA}/${item.mainLogo.id}/download`, {
                  responseType: "blob",
                });
                mainLogoUrl = URL.createObjectURL(imgRes.data);
              } catch (error) {
                console.error("Error loading main logo:", error);
                mainLogoUrl = "/images/about_us.webp"; // Fallback image
              }
            } else {
              mainLogoUrl = "/images/about_us.webp"; // Fallback image
            }

            // Load about images
            const aboutWithImages = await Promise.all(
              (item.about || []).map(async (abt) => {
                let imgUrl = "";
                if (abt.img?.id && abt.isActive) {
                  try {
                    const imgRes = await axios.get(`${API_MEDIA}/${abt.img.id}/download`, {
                      responseType: "blob",
                    });
                    imgUrl = URL.createObjectURL(imgRes.data);
                  } catch (error) {
                    console.error("Error loading about image:", error);
                    imgUrl = ""; // No fallback for about images
                  }
                }
                return {
                  ...abt,
                  imgUrl
                };
              })
            );

            return {
              ...item,
              mainLogoUrl,
              about: aboutWithImages.filter(abt => abt.isActive) // Only active about items
            };
          })
        );

        setMissionVisionData(dataWithImages);
      } catch (error) {
        console.error("Error fetching mission/vision data:", error);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    };

    fetchMissionVisionData();
  }, []);

  // Gradient text style
  const gradientText = {
    background: "linear-gradient(90deg, #eab308, #ffffff, #eab308)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  };

  // Lightning effect animation for headings
  const lightningVariants = {
    animate: {
      textShadow: [
        "0 0 5px rgba(255, 255, 255, 0.5)",
        "0 0 15px rgba(234, 179, 8, 0.8)",
        "0 0 25px rgba(234, 179, 8, 1)",
        "0 0 15px rgba(234, 179, 8, 0.8)",
        "0 0 5px rgba(255, 255, 255, 0.5)",
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      },
    },
  };

  // Animation variants for cards
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    hover: { 
      scale: 1.05,
      boxShadow: "0 8px 24px rgba(234, 179, 8, 0.6)",
      transition: { duration: 0.3 }
    },
  };

  if (loading) {
    return (
      <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-yellow-400 text-4xl mx-auto mb-4" />
          <p className="text-gray-300">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-yellow-500 text-gray-900 px-6 py-2 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If no data is available from API
  if (missionVisionData.length === 0) {
    return (
      <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-300 text-xl mb-4">No content available</p>
          <p className="text-gray-400">Please add mission and vision data through the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen relative overflow-hidden">
      {/* Subtle Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black opacity-50 z-0" />

      {/* Header Section with Title, Description, and Logo */}
      {missionVisionData.length > 0 && (
        <section className="py-16 px-10 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="md:w-1/2">
                <motion.h1
                  className="text-4xl md:text-5xl font-bold text-left mb-4"
                  style={gradientText}
                  variants={lightningVariants}
                  animate="animate"
                >
                  {missionVisionData[0].heading}
                </motion.h1>
                <motion.p
                  className="text-gray-300 text-lg leading-relaxed line-clamp-5"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  {missionVisionData[0].subheading}
                </motion.p>
              </div>
              <div className="md:w-1/2 flex justify-center">
                <motion.img
                  src={missionVisionData[0].mainLogoUrl}
                  alt={missionVisionData[0].heading}
                  className="w-64 md:w-96 h-auto rounded-2xl shadow-2xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  onError={(e) => {
                    e.target.src = "/images/about_us.webp";
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Mission, Vision, and Values Cards Section */}
      <section className="py-20 px-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
          {missionVisionData[0].about && missionVisionData[0].about.length > 0 ? (
            missionVisionData[0].about.map((item, index) => (
              <motion.div
                key={item.id || index}
                className="bg-gray-800 p-8 rounded-2xl flex flex-col items-center text-center hover:bg-gray-700 transition-all duration-300 group"
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover="hover"
              >
                {/* Image Display */}
                {item.imgUrl && (
                  <motion.div 
                    className="mb-4 w-20 h-20 rounded-full overflow-hidden border-2 border-yellow-400 group-hover:border-yellow-300 transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    <img
                      src={item.imgUrl}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                )}
                
                <motion.h2
                  className="text-2xl font-bold mb-4"
                  style={gradientText}
                  variants={lightningVariants}
                  animate="animate"
                >
                  {item.title}
                </motion.h2>
                <p className="text-gray-300 text-base leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))
          ) : (
            // Show message if no about items are available
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400 text-lg">No about items available</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;